import Button from "@/components/Common/Button";
import useAdditionalLimitRequest from "@/hooks/useAdditionalLimitRequest";
import useBuyoutBundlePurchase from "@/hooks/useBuyoutBundlePurchase";
import AddMoreBuyoutsContent from "@/pages/plans/AddMoreBuyoutsContent";
import RequestMoreContent from "@/pages/plans/RequestMoreContent";
import {useSettingsAgencyStore} from "@/store/agency";
import {useSettingsCompanyStore} from "@/store/company";
import type {TFloatingBuyouts, TSettingsScope} from "@/store/types";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const REQUEST_TYPES: Record<"EXTRA_BUYOUTS" | "EXTRA_CAMPAIGNS", TFloatingBuyouts> = {
  EXTRA_BUYOUTS: "buyouts",
  EXTRA_CAMPAIGNS: "campaigns",
};

const AddMoreBuyoutsSubview: FC<{onClose(): void; scope: TSettingsScope; type: keyof typeof REQUEST_TYPES}> = ({
  onClose,
  scope,
  type,
}) => {
  const {t} = useTranslation("common");
  const company = useSettingsCompanyStore(state => state.company);
  const scopeAgency = useSettingsAgencyStore(state => state.agency);

  const isAgencyScope = scope.type === "agency";
  const companyId = isAgencyScope ? undefined : scope.id;
  const isSubbrand = !!companyId && company.company_access_level === "SUB_BRAND";
  const scopeHolder = isAgencyScope ? scopeAgency : company;

  const purchase = useBuyoutBundlePurchase({
    agencyId: isAgencyScope ? scope.id : company.agency?.id,
    companyId,
    credits: +((isAgencyScope ? scopeAgency.credits : company.agency?.credits) ?? 0).toFixed(2),
    currency: scopeHolder.package_subscription?.currency ?? scopeHolder.currency,
    onSuccess: onClose,
    type,
  });
  const limitRequest = useAdditionalLimitRequest({companyId: companyId!, type: REQUEST_TYPES[type]});

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        {isSubbrand ? (
          <RequestMoreContent type={REQUEST_TYPES[type]} />
        ) : (
          <AddMoreBuyoutsContent purchase={purchase} type={type} />
        )}
      </div>
      <div className="flex gap-3">
        <Button buttonStyle="White" className="h-10 flex-1 px-5 text-sm sm:flex-none" onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button
          buttonStyle="Gradient"
          className="h-10 flex-1 px-5 text-sm sm:flex-none"
          isDisabled={!isSubbrand && purchase.selectedOption === null}
          isLoading={isSubbrand ? limitRequest.isPending : purchase.isPending}
          onClick={
            isSubbrand
              ? async () => {
                  await limitRequest.request();
                  onClose();
                }
              : purchase.purchase
          }>
          {t("confirm")}
        </Button>
      </div>
    </div>
  );
};

export default AddMoreBuyoutsSubview;
